<template>
  <div class="viq-lens-builder">
    <!-- Header -->
    <div class="viq-lens-builder__header">
      <div class="viq-lens-builder__header-left">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        <span>Lens Builder</span>
      </div>
      <button class="viq-lens-builder__close" @click="close">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Chat area -->
    <div ref="chatArea" class="viq-lens-builder__chat">
      <!-- Welcome message -->
      <div v-if="messages.length === 0 && !loading" class="viq-lens-builder__welcome">
        <p>Describe the lens you want to create.</p>
        <p class="viq-lens-builder__welcome-hint">
          e.g. &ldquo;I need a lens for tracking Malta gambling regulations&rdquo;
        </p>
      </div>

      <!-- Messages -->
      <div v-for="(msg, i) in messages" :key="i" class="viq-lens-builder__msg-row">
        <!-- User bubble -->
        <div v-if="msg.role === 'user'" class="viq-lens-builder__msg viq-lens-builder__msg--user">
          <div class="viq-lens-builder__bubble viq-lens-builder__bubble--user">
            {{ msg.content }}
          </div>
        </div>

        <!-- Assistant bubble + config card -->
        <div v-if="msg.role === 'assistant'" class="viq-lens-builder__msg viq-lens-builder__msg--assistant">
          <div class="viq-lens-builder__bubble viq-lens-builder__bubble--assistant">
            {{ msg.content }}
          </div>

          <!-- Config preview card -->
          <div v-if="msg.config" class="viq-lens-builder__config-card">
            <div class="viq-lens-builder__config-header">
              <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
                <path d="M7 1L9 5.5L14 6.5L10.5 10L11.5 14L7 12L2.5 14L3.5 10L0 6.5L5 5.5L7 1Z" fill="currentColor" opacity="0.6" />
              </svg>
              <div>
                <div class="viq-lens-builder__config-name">{{ msg.config.name }}</div>
                <div class="viq-lens-builder__config-desc">{{ msg.config.description }}</div>
              </div>
            </div>

            <!-- Instructions preview -->
            <div v-if="msg.config.system_instructions" class="viq-lens-builder__config-section">
              <div class="viq-lens-builder__config-label">Instructions</div>
              <div class="viq-lens-builder__config-text">{{ msg.config.system_instructions }}</div>
            </div>

            <!-- Filters & format badges -->
            <div class="viq-lens-builder__config-badges">
              <span
                v-if="msg.config.output_format && msg.config.output_format.style"
                class="viq-lens-builder__badge viq-lens-builder__badge--style"
              >
                {{ formatOutputStyle(msg.config.output_format.style) }}
              </span>
              <span
                v-if="msg.config.output_format && msg.config.output_format.length"
                class="viq-lens-builder__badge viq-lens-builder__badge--length"
              >
                {{ msg.config.output_format.length }}
              </span>
              <span
                v-for="ct in (msg.config.default_filters && msg.config.default_filters.content_types) || []"
                :key="'ct-' + ct"
                class="viq-lens-builder__badge viq-lens-builder__badge--content"
              >
                {{ ct.replace(/_/g, ' ') }}
              </span>
              <span
                v-for="j in (msg.config.default_filters && msg.config.default_filters.jurisdictions) || []"
                :key="'j-' + j"
                class="viq-lens-builder__badge viq-lens-builder__badge--jurisdiction"
              >
                {{ j }}
              </span>
            </div>

            <!-- Pinned content -->
            <div
              v-if="msg.config.pinned_content_ids && msg.config.pinned_content_ids.length > 0"
              class="viq-lens-builder__config-pinned"
            >
              {{ msg.config.pinned_content_ids.length }} pinned content item{{ msg.config.pinned_content_ids.length !== 1 ? 's' : '' }}
            </div>

            <!-- Intent tags -->
            <div
              v-if="msg.config.intent_tags && msg.config.intent_tags.length > 0"
              class="viq-lens-builder__config-tags"
            >
              <span
                v-for="tag in msg.config.intent_tags"
                :key="tag"
                class="viq-lens-builder__tag"
              >
                #{{ tag }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading indicator -->
      <div v-if="loading" class="viq-lens-builder__msg viq-lens-builder__msg--assistant">
        <div class="viq-lens-builder__bubble viq-lens-builder__bubble--loading">
          <span class="viq-lens-builder__spinner" />
          <span v-if="stage === 'analyzing'">Analysing your request...</span>
          <span v-else-if="stage === 'searching'">Finding relevant content...</span>
          <span v-else-if="stage === 'building'">Building your lens...</span>
          <span v-else-if="stage === 'refining'">Refining the lens...</span>
          <span v-else>Thinking...</span>
        </div>
      </div>

      <!-- Error -->
      <div v-if="error" class="viq-lens-builder__error">{{ error }}</div>
    </div>

    <!-- Confirm bar -->
    <div v-if="currentConfig && !loading" class="viq-lens-builder__confirm">
      <button
        class="viq-lens-builder__confirm-btn"
        :disabled="confirming"
        @click="confirmLens"
      >
        {{ confirming ? 'Saving...' : `Create "${currentConfig.name}"` }}
      </button>
    </div>

    <!-- Input -->
    <div class="viq-lens-builder__input-area">
      <input
        ref="lensInput"
        v-model="input"
        type="text"
        :placeholder="inputPlaceholder"
        :disabled="loading || confirming"
        @keyup.enter="handleSend"
      />
      <button
        class="viq-lens-builder__send"
        :disabled="loading || confirming || !input.trim()"
        @click="handleSend"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import { createViqApi } from "@/services/viq-api";

export default {
  name: "ViqLensBuilder",
  data() {
    return {
      messages: [],
      input: "",
      loading: false,
      stage: null,
      currentConfig: null,
      confirming: false,
      error: null,
    };
  },
  computed: {
    inputPlaceholder() {
      return this.currentConfig
        ? "Refine \u2014 e.g. \"focus on Malta only\", \"add checklist format\"..."
        : "Describe your lens \u2014 e.g. \"Track PSD3 developments across Europe\"...";
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.lensInput) this.$refs.lensInput.focus();
    });
  },
  methods: {
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatArea;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    handleSend() {
      const text = this.input.trim();
      if (!text || this.loading) return;
      this.input = "";
      this.error = null;

      this.messages.push({ role: "user", content: text });
      this.scrollToBottom();
      this.loading = true;
      this.stage = null;

      const isRefine = this.currentConfig !== null;
      const api = createViqApi(this);
      const token = this.$store.state.Auth.access_token;

      const endpoint = isRefine ? "/lenses/refine/stream" : "/lenses/build/stream";
      const payload = isRefine
        ? { current_config: this.currentConfig, instruction: text }
        : { name: text, description: text };

      let newConfig = null;
      let assistantMessage = "";

      api.stream(
        endpoint,
        payload,
        {
          onMeta: () => {},
          onStatus: (data) => {
            if (data && data.stage) this.stage = data.stage;
            this.scrollToBottom();
          },
          onConfig: (data) => {
            newConfig = data;
          },
          onText: (data) => {
            const t = typeof data === "string" ? data : data.text || "";
            assistantMessage += t;
          },
          onDone: (data) => {
            if (data && data.lens) {
              newConfig = data.lens;
            }
            if (data && data.config) {
              newConfig = data.config;
            }
            if (newConfig) {
              this.currentConfig = newConfig;
              this.messages.push({
                role: "assistant",
                content: assistantMessage || "Here's what I've built for you.",
                config: newConfig,
              });
            } else if (!this.error) {
              this.error = "Failed to generate lens configuration.";
            }
            this.loading = false;
            this.stage = null;
            this.scrollToBottom();
          },
          onError: (err) => {
            this.error = err.message || "Connection error. Please try again.";
            this.loading = false;
            this.stage = null;
          },
        },
        token
      );
    },
    async confirmLens() {
      if (!this.currentConfig || this.confirming) return;
      this.confirming = true;
      try {
        const api = createViqApi(this);
        const lens = await api.createLens(this.currentConfig);
        this.$store.commit("Viq/ADD_LENS", lens);
        this.$store.commit("Viq/SET_ACTIVE_LENS", lens);
        this.$store.commit("Viq/SET_MODE", "ask");
      } catch (err) {
        this.error = "Failed to save lens. Please try again.";
      } finally {
        this.confirming = false;
      }
    },
    formatOutputStyle(style) {
      const map = {
        default: "Default",
        executive_summary: "Executive Summary",
        detailed_analysis: "Detailed Analysis",
        comparative: "Comparative",
        timeline: "Timeline",
        checklist: "Checklist",
        forward_looking: "Forward-Looking",
      };
      return map[style] || style;
    },
    close() {
      this.$store.commit("Viq/SET_MODE", "ask");
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-lens-builder {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 420px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: $viq-space-sm $viq-space-md;
    border-bottom: 1px solid $viq-border;
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: $viq-font-sm;
    font-weight: 600;
    color: $viq-text-primary;

    svg { color: $viq-accent; }
  }

  &__close {
    display: flex;
    align-items: center;
    background: none;
    border: none;
    color: $viq-text-muted;
    cursor: pointer;
    padding: 4px;
    border-radius: $viq-radius-sm;
    transition: color $viq-transition-fast;

    &:hover { color: $viq-text-primary; }
  }

  /* Chat area */
  &__chat {
    flex: 1;
    overflow-y: auto;
    padding: $viq-space-md;
    display: flex;
    flex-direction: column;
    gap: $viq-space-sm;
    min-height: 200px;
  }

  &__welcome {
    text-align: center;
    padding: $viq-space-xl 0;

    p {
      font-size: $viq-font-sm;
      color: $viq-text-secondary;
      margin: 0;
    }
  }

  &__welcome-hint {
    font-size: $viq-font-xs !important;
    color: $viq-text-muted !important;
    margin-top: $viq-space-xs !important;
  }

  /* Message bubbles */
  &__msg-row {
    display: flex;
    flex-direction: column;
    gap: $viq-space-xs;
  }

  &__msg {
    display: flex;
    flex-direction: column;
  }

  &__bubble {
    font-size: $viq-font-xs;
    padding: $viq-space-sm;
    border-radius: $viq-radius-md;
    line-height: 1.5;
    word-wrap: break-word;

    &--user {
      background: $viq-accent;
      color: #fff;
      border-radius: $viq-radius-md;
      margin-bottom: $viq-space-xs;
    }

    &--assistant {
      background: $viq-bg-secondary;
      color: $viq-text-primary;
      border-radius: $viq-radius-md;
    }

    &--loading {
      background: $viq-bg-secondary;
      color: $viq-text-secondary;
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: $viq-font-xs;
    }
  }

  /* Config preview card */
  &__config-card {
    margin-top: $viq-space-xs;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-md;
    padding: $viq-space-sm;
    background: $viq-bg-primary;
  }

  &__config-header {
    display: flex;
    align-items: flex-start;
    gap: $viq-space-sm;
    margin-bottom: $viq-space-sm;

    svg { color: $viq-accent; flex-shrink: 0; margin-top: 2px; }
  }

  &__config-name {
    font-size: $viq-font-sm;
    font-weight: 600;
    color: $viq-text-primary;
  }

  &__config-desc {
    font-size: $viq-font-xs;
    color: $viq-text-secondary;
  }

  &__config-section {
    margin-bottom: $viq-space-sm;
  }

  &__config-label {
    font-size: 10px;
    font-weight: 600;
    color: $viq-text-muted;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 2px;
  }

  &__config-text {
    font-size: $viq-font-xs;
    color: $viq-text-secondary;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__config-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: $viq-space-xs;
  }

  &__badge {
    font-size: 10px;
    padding: 2px 8px;
    border-radius: 10px;
    font-weight: 500;

    &--style {
      background: rgba($viq-accent, 0.1);
      color: $viq-accent;
    }
    &--length {
      background: $viq-bg-secondary;
      color: $viq-text-secondary;
      text-transform: capitalize;
    }
    &--content {
      background: rgba(59, 130, 246, 0.08);
      color: #2563eb;
      text-transform: capitalize;
    }
    &--jurisdiction {
      background: rgba(217, 119, 6, 0.08);
      color: #d97706;
    }
  }

  &__config-pinned {
    font-size: $viq-font-xs;
    color: $viq-text-muted;
  }

  &__config-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: $viq-space-xs;
  }

  &__tag {
    font-size: 10px;
    color: $viq-text-muted;
    background: $viq-bg-secondary;
    border: 1px solid $viq-border;
    padding: 1px 6px;
    border-radius: $viq-radius-sm;
  }

  &__spinner {
    width: 12px;
    height: 12px;
    border: 2px solid $viq-border;
    border-top-color: $viq-accent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    flex-shrink: 0;
  }

  &__error {
    font-size: $viq-font-xs;
    color: $viq-error;
    text-align: center;
    padding: $viq-space-xs 0;
  }

  /* Confirm bar */
  &__confirm {
    padding: $viq-space-sm $viq-space-md;
    border-top: 1px solid $viq-border;
  }

  &__confirm-btn {
    width: 100%;
    padding: $viq-space-sm $viq-space-md;
    background: $viq-accent;
    color: #fff;
    border: none;
    border-radius: $viq-radius-md;
    font-size: $viq-font-sm;
    font-weight: 600;
    cursor: pointer;
    transition: background $viq-transition-fast;

    &:hover { background: darken($viq-accent, 8%); }
    &:disabled { opacity: 0.5; cursor: default; }
  }

  /* Input area */
  &__input-area {
    display: flex;
    gap: 8px;
    padding: $viq-space-sm $viq-space-md;
    border-top: 1px solid $viq-border;

    input {
      flex: 1;
      font-size: $viq-font-sm;
      border: 1px solid $viq-border;
      border-radius: $viq-radius-md;
      padding: $viq-space-sm $viq-space-md;
      outline: none;
      transition: border-color $viq-transition-fast;

      &:focus { border-color: $viq-accent; }
      &:disabled { opacity: 0.5; }
      &::placeholder { color: $viq-text-muted; }
    }
  }

  &__send {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: $viq-space-sm $viq-space-md;
    background: $viq-accent;
    color: #fff;
    border: none;
    border-radius: $viq-radius-md;
    cursor: pointer;
    transition: background $viq-transition-fast;

    &:hover { background: darken($viq-accent, 8%); }
    &:disabled { opacity: 0.5; cursor: default; }
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
